import { FeatureInfoStore, FeatureWindowPresentation } from "../../apps/map/Map.types";
import { LayerSelectorViews } from "../../apps/map/components/layerselector/LayerSelector.types";
import { MapWidgetPosition } from "../../apps/map/components/widgets/MapWidget.types";
import { WMSListPresentation } from "../../apps/map/components/wmslist/WMSList.types";
import IS_EFAS from "./Environment";

export enum LoginState {
    Failed,
    InProgress,
    Success
}

export enum ActiveLayersOpacity {
    DROPDOWN = "DD",
    ALWAYS_VISIBLE = "AV"
}

export enum ActiveLayersReordering {
    ARROWS = "AR",
    DRAGGING = "DR"
}

export interface WebSettingPreset {
    [name: string]: { date: string, value: string }
}
export type WMSTileSize = 'default' | '256' | '512';

export interface ExternalWMSConfig {
    url: string,
    name: string,
    params: string,
    version: string,
    featureInfoFormat: string
    capabilitiesFormat: string
    latestForecastLayer: string
    tileSize?: WMSTileSize
    gutter?: number
}
interface WebSettings {
    active_layers: string[]
    active_layers_opacity: ActiveLayersOpacity;
    active_layers_reordering: ActiveLayersReordering;
    base_map: string
    external_wms_list: ExternalWMSConfig[]
    feature_info_window_dimensions: FloatingWindowDimensions
    forecast_date_position: MapWidgetPosition
    active_notifications_position: MapWidgetPosition
    legend_window_dimensions: FloatingWindowDimensions
    wms_list_presentation: WMSListPresentation
    feature_info_presentation: FeatureWindowPresentation
    activenotif: string
    presets: WebSettingPreset
    accepted_welcome_message: boolean;
    layer_selector_view: LayerSelectorViews;
    use_map_markers: boolean;
    remember_map_markers: boolean;
    close_removes_point: boolean;
    active_map_pins: Record<string, FeatureInfoStore>;
    favourite_layers: Set<string>;
    tos_accepted: boolean;
}

export interface UserI {
    readonly user_id: number;
    readonly username: string;
    readonly first_name: string;
    readonly last_name: string;
    readonly email: string;
    readonly signature: string;
    readonly settings: Record<string, unknown>;
    readonly token: string;
    readonly editor: boolean;
    readonly forecaster: boolean;
    readonly partner_abbr: string;
    readonly partner_name: string;
    readonly partner_country: string;
    readonly cartlength: number;
    readonly unread_message_count: number;
    readonly web_settings: WebSettings;
    readonly groups: string[];
    readonly communications_opt_in: boolean;
}

export interface FloatingWindowDimensions {
    startX?: number
    startY?: number
    width?: number
    height?: number
}

export class User implements UserI {
    readonly loggedIn: LoginState;
    readonly user_id: number = -1;
    readonly username: string = "";
    readonly first_name: string = "";
    readonly last_name: string = "";
    readonly email: string = "";
    readonly signature: string = "";
    readonly settings: Record<string, unknown> = {};
    readonly token: string = "";
    readonly editor: boolean = false;
    readonly forecaster: boolean = false;
    readonly partner_abbr: string = "";
    readonly partner_name: string = "";
    readonly partner_country: string = "";
    readonly cartlength: number = 0;
    readonly unread_message_count: number = 0;
    readonly groups: string[] = [];
    readonly communications_opt_in: boolean = true;
    readonly web_settings = {
        active_layers: [] as string[],
        base_map: "OSM",
        external_wms_list: [] as ExternalWMSConfig[],
        feature_info_window_dimensions: {},
        feature_info_presentation: FeatureWindowPresentation.LIST,
        forecast_date_position: MapWidgetPosition.RIGHT,
        active_notifications_position: MapWidgetPosition.HIDDEN,
        legend_window_dimensions: {},
        wms_list_presentation: IS_EFAS ? WMSListPresentation.ICONS : WMSListPresentation.TEXT,
        activenotif: "off",
        presets: {},
        accepted_welcome_message: false,
        layer_selector_view: LayerSelectorViews.LIST,
        use_map_markers: true,
        remember_map_markers: false,
        active_map_pins: {},
        favourite_layers: new Set<string>([]),
        tos_accepted: false,
        activeLayersOpacity: ActiveLayersOpacity.ALWAYS_VISIBLE,
        activeLayersReordering: ActiveLayersReordering.DRAGGING,
    };

    constructor(loggedIn: LoginState, user?: Partial<UserI>) {
        this.loggedIn = loggedIn;
        if (user) {
            this.user_id = user.user_id ?? this.user_id;
            this.username = user.username ?? this.username;
            this.email = user.email ?? this.email;
            this.first_name = user.first_name ?? this.first_name;
            this.last_name = user.last_name ?? this.last_name;
            this.signature = user.signature ?? this.signature;
            this.settings = user.settings ?? this.settings;
            this.token = user.token ?? this.token;
            this.editor = user.editor ?? this.editor;
            this.forecaster = user.forecaster ?? this.forecaster;
            this.partner_abbr = user.partner_abbr ?? this.partner_abbr;
            this.partner_name = user.partner_name ?? this.partner_name;
            this.partner_country = user.partner_country ?? this.partner_country;
            this.cartlength = user.cartlength ?? this.cartlength;
            this.unread_message_count = user.unread_message_count ?? this.unread_message_count;
            this.groups = user.groups ?? this.groups;
            this.communications_opt_in = user.communications_opt_in ?? this.communications_opt_in;

            if (user.web_settings)
                this.web_settings = user.web_settings;
        }
    }
}
export interface ExtraMenuEntry {
    name: string
    url: string
    active?: boolean
    external?: boolean
}

export interface OutletIProps {
    setExtraMenuLinks: (links: ExtraMenuEntry[], activeName?: string) => void
    refreshUser: () => void
    user: User
    matomo: Matomo
}

interface MatomoDataLayer {
    push: (args: string[]) => void;
}
export class Matomo {
    private matomo: MatomoDataLayer | null = null;
    constructor() {
        if (Object.prototype.hasOwnProperty.call(window, "_paq")) {
            this.matomo = (window as unknown as { _paq: MatomoDataLayer })._paq;
        }
    }

    trackLayerAdd(title: string, date: string) {
        if (this.matomo) {
            this.matomo.push(['trackEvent', 'Layer', 'Add', title, date]);
        }
    }

    error(...message: unknown[]) {
        console.error(...message);
    }
}

export interface GlofasCarousel {
    id: number
    background_image: string
    body: string
    button_text: string
    footer: string
    date: string
    order: number
    published: string
}

export interface GlofasNews {
    id: number
    slug: string
    created: string
    modified: string
    title: string
    body: string
    published: boolean
    image: string
    category: number
    sender: number
}